/**
 * PDF-Vorschau fuer die Preislisten-Kacheln im Downloadcenter.
 *
 * Die Preislisten werden als Dateigalerie-Sitepart vom Eltern-Theme gerendert
 * (`.downloads__pricelist .filegallery > div > a.filegalleryFile`) und liefern
 * nur den PDF-Link im href, ein Icon und einen Beschreibungstext - keine
 * Vorschau-Daten. Dieses Modul rueckt jede Kachel um eine Media-Box auf und
 * rendert lazy die erste PDF-Seite als "Dokument-Ausschnitt oben".
 *
 * Wiederverwendung: queuePdfPreview() kapselt PDF.js-Laden, Concurrency-Queue,
 * persistenten Cache, Server-Thumbnail-Upload und CSP-konforme data:-URLs.
 *
 * Vanilla JS, kein jQuery.
 */
import { queuePdfPreview } from './pdfPreview.js';

export function initPricelistPreview() {
    const tiles = Array.from(
        document.querySelectorAll('.downloads__pricelist .filegalleryFile')
    );
    if (!tiles.length) return; // nur auf der Downloadcenter-Seite vorhanden

    // Es gibt nur eine Handvoll Preislisten und die Vorschauen liegen oben
    // (above the fold). Daher nicht lazy per IntersectionObserver, sondern direkt
    // rendern - die Concurrency-Queue in pdfPreview.js (max. 3 parallel) drosselt
    // ohnehin, und statische Server-Thumbnails laden praktisch sofort.
    tiles.forEach(tile => {
        // Kataloge wie die Dateien im Downloadcenter im neuen Tab oeffnen,
        // statt sie per download-Attribut des Eltern-Themes herunterzuladen.
        tile.removeAttribute('download');
        tile.target = '_blank';
        tile.rel = 'noopener';
        prepareMedia(tile);
        ensurePreview(tile);
    });
}

// Media-Box aufbauen und das vorhandene Placeholder-Icon hineinschieben.
// Idempotent: laeuft pro Kachel nur einmal.
function prepareMedia(tile) {
    if (tile.querySelector('.filegalleryFile__media')) return;

    // Bewusst ein <div> (kein <span>): die Textregel `.filegalleryFile span`
    // hat durch den Element-Selektor hoehere Spezifitaet und wuerde sonst
    // display/overflow der Media-Box ueberschreiben (Canvas liefe ungeclippt raus).
    const media = document.createElement('div');
    media.className = 'filegalleryFile__media';

    const icon = tile.querySelector('.icon');
    if (icon) media.appendChild(icon); // Placeholder wandert in die Media-Box

    tile.insertBefore(media, tile.firstChild);
}

// Erste PDF-Seite fuer eine sichtbare Kachel erzeugen.
function ensurePreview(tile) {
    if (tile.dataset.previewState) return; // bereits angestossen/erledigt

    const href = (tile.getAttribute('href') || '').trim();
    if (href === '' || !/\.pdf(\?|#|$)/i.test(href)) return;

    tile.dataset.previewState = 'loading';
    const media = tile.querySelector('.filegalleryFile__media');
    if (media) media.classList.add('is-loading');

    // 1) Zuerst einen bereits serverseitig gespeicherten Thumbnail versuchen
    //    (spart den PDF.js-Download, wenn ihn ein anderer Besucher erzeugt hat).
    //    Basename-Ableitung wie in pdfPreview.js; der Server haengt .jpg an.
    const base = decodeURIComponent(
        (href.split('?')[0].split('#')[0].split('/').pop() || '')
    );
    const thumbUrl = '/userdata/filegallery/thumbs/' + base + '.jpg';

    const probe = new Image();
    probe.onload = () => setPreview(tile, media, probe);
    probe.onerror = () => renderWithPdfJs(tile, media, href);
    probe.src = thumbUrl;
}

// Fallback: erste Seite per PDF.js rendern (Cache + Server-Upload laufen mit).
function renderWithPdfJs(tile, media, href) {
    queuePdfPreview(href)
        .then(previewEl => setPreview(tile, media, previewEl))
        .catch(() => {
            if (media) media.classList.remove('is-loading');
            tile.dataset.previewState = 'error'; // Placeholder-Icon bleibt sichtbar
        });
}

function setPreview(tile, media, previewEl) {
    if (!media) return;
    const placeholder = media.querySelector('.icon');
    if (placeholder) placeholder.remove();

    previewEl.classList.add('filegalleryFile__preview');
    previewEl.setAttribute('aria-hidden', 'true');
    media.appendChild(previewEl);
    media.classList.remove('is-loading');
    tile.dataset.previewState = 'done';
}
